export type QuestionTypeCode = 'RA' | 'RS' | 'DI' | 'RL' | 'RTS' | 'SGD' | 'SWT' | 'WE' | 'FIB_R' | 'FIB_RW' | 'RO' | 'SST' | 'WFD' | 'ASQ' | 'FIB_L';
export type Section = 'speaking' | 'writing' | 'reading' | 'listening';
export type QuestionStatus = 'draft' | 'ready' | 'published' | 'offline' | 'archived';
export type Difficulty = 'easy' | 'medium' | 'hard';

export interface QuestionType {
  code: QuestionTypeCode;
  nameEn: string;
  nameCn: string;
  section: Section;
  answerMode: string;
  requiresMicrophone: boolean;
  totalCount: number;
  highFrequencyCount?: number;
  sortOrder: number;
  isActive: boolean;
  settings?: {
    showReferenceAnswer: boolean;
    showQuestionDifficulty: boolean;
    practiceInstructionEn?: string | null;
    mockInstructionEn?: string | null;
  } | null;
}

export interface Question {
  id: string;
  questionTypeCode: QuestionTypeCode;
  title: string;
  difficulty: Difficulty;
  status: QuestionStatus;
  progressStatus: 'not_started' | 'started' | 'done';
  attemptCount: number;
  legacyQuestionId?: string | null;
  legacyQIdx?: number | null;
  isHighFrequency?: boolean;
  isNew?: boolean;
}

export type ComponentType =
  | 'audio-player'
  | 'recording-control'
  | 'text-input'
  | 'text-area'
  | 'image-display'
  | 'passage-display'
  | 'fill-blank'
  | 'drag-blank'
  | 'paragraph-reorder'
  | 'timer'
  | 'word-counter';

export interface PracticeAttempt {
  id: string;
  questionId: string;
  status: 'started' | 'submitted' | 'failed';
  renderMode: 'native' | 'iframe';
  durationMs?: number;
  clientEventId: string;
}

export interface UserProgress {
  questionId: string;
  status: 'not_started' | 'started' | 'done';
  lastAttemptId?: string;
}

export const QUESTION_TYPE_META: Record<QuestionTypeCode, { nameEn: string; nameCn: string; section: Section; requiresMicrophone: boolean }> = {
  RA: { nameEn: 'Read Aloud', nameCn: '朗读段落', section: 'speaking', requiresMicrophone: true },
  RS: { nameEn: 'Repeat Sentence', nameCn: '复述句子', section: 'speaking', requiresMicrophone: true },
  DI: { nameEn: 'Describe Image', nameCn: '描述图片', section: 'speaking', requiresMicrophone: true },
  RL: { nameEn: 'Retell Lecture', nameCn: '复述讲座', section: 'speaking', requiresMicrophone: true },
  RTS: { nameEn: 'Respond to Situation', nameCn: '应对情景', section: 'speaking', requiresMicrophone: true },
  SGD: { nameEn: 'Summarize Group Discussion', nameCn: '总结讨论', section: 'speaking', requiresMicrophone: true },
  SWT: { nameEn: 'Summarize Written Text', nameCn: '总结文章', section: 'writing', requiresMicrophone: false },
  WE: { nameEn: 'Write Essay', nameCn: '写作文章', section: 'writing', requiresMicrophone: false },
  FIB_R: { nameEn: 'Reading Fill in Blanks', nameCn: '阅读填空', section: 'reading', requiresMicrophone: false },
  FIB_RW: { nameEn: 'R&W Fill in Blanks', nameCn: '读写填空', section: 'reading', requiresMicrophone: false },
  RO: { nameEn: 'Reorder Paragraphs', nameCn: '段落排序', section: 'reading', requiresMicrophone: false },
  SST: { nameEn: 'Summarize Spoken Text', nameCn: '总结口述', section: 'listening', requiresMicrophone: false },
  WFD: { nameEn: 'Write from Dictation', nameCn: '听写句子', section: 'listening', requiresMicrophone: false },
  ASQ: { nameEn: 'Answer Short Question', nameCn: '简答问题', section: 'speaking', requiresMicrophone: true },
  FIB_L: { nameEn: 'Listening Fill in Blanks', nameCn: '听力填空', section: 'listening', requiresMicrophone: false },
};

export const QUESTION_TYPE_ORDER: QuestionTypeCode[] = [
  'RA',
  'RS',
  'DI',
  'RTS',
  'SGD',
  'RL',
  'ASQ',
  'SWT',
  'WE',
  'FIB_R',
  'FIB_RW',
  'RO',
  'SST',
  'WFD',
  'FIB_L',
];

export function getQuestionTypeMeta(code: string) {
  return QUESTION_TYPE_META[code as QuestionTypeCode] || {
    nameEn: code,
    nameCn: code,
    section: 'speaking' as Section,
    requiresMicrophone: false,
  };
}

export function questionTypeRequiresMicrophone(code: string) {
  return Boolean(getQuestionTypeMeta(code).requiresMicrophone);
}

// Section color mapping
export const SECTION_COLORS: Record<Section, { primary: string; light: string }> = {
  speaking: { primary: 'var(--color-speaking)', light: 'var(--color-speaking-light)' },
  writing: { primary: 'var(--color-writing)', light: 'var(--color-writing-light)' },
  reading: { primary: 'var(--color-reading)', light: 'var(--color-reading-light)' },
  listening: { primary: 'var(--color-listening)', light: 'var(--color-listening-light)' },
};

export const SECTION_LABELS: Record<Section, string> = {
  speaking: '口语 Speaking',
  writing: '写作 Writing',
  reading: '阅读 Reading',
  listening: '听力 Listening',
};
